import Vue from "vue"

import { extractCaseIdFromChannelURL } from "@/common/common_actions.js"

const indices = (taskIdStr, state) => {
  const tid = taskIdParts(taskIdStr)
  const tasklistIndex = state.currentlySelected.case.tasklists.findIndex((tl) => tasklistIdParts(tl.id).tasklistId === tid.tasklistId)
  const taskIndex = state.currentlySelected.case.tasklists[tasklistIndex].tasks.findIndex((t) => taskIdParts(t.id).taskId === tid.taskId)
  return { tasklistIndex, taskIndex }
}
const taskIdParts = (taskIdStr) => {
  const p = taskIdStr.split("-")
  return { type: p[0], caseId: p[1], tasklistId: p[2], taskId: p[3] }
}
const tasklistIdParts = (taskIdStr) => {
  const p = taskIdStr.split("-")
  return { type: p[0], caseId: p[1], tasklistId: p[2] }
}

export default {
  taskStarted(state) {
    state.taskRunning = true
  },
  taskComplete(state) {
    state.taskRunning = false
  },
  taskUpdate(state, result) {
    state.taskResult = result
  },
  setTasks(state, tasks) {
    state.tasks = tasks
  },
  setCases(state, cases) {
    state.all = cases
  },
  requestingCases(state) {
    state.loading = true
  },
  receivedCases(state) {
    state.loading = false
  },
  requestingCase(state) {
    state.currentlySelected.loading = true
  },
  receivedCase(state) {
    state.currentlySelected.loading = false
  },
  savingCase(state) {
    state.currentlySelected.loading = true
  },
  savedCase(state) {
    state.currentlySelected.loading = false
  },
  setCurrentlySelectedCase(state, currentCase) {
    if (currentCase) {
      const currentlyViewing = currentCase.currentlyViewing || []
      delete currentCase.currentlyViewing
      if (!currentCase.conversations) {
        // findCase does not fetch conversations; they are loaded via loadCaseConversationsPage.
        // If that request finishes before getCurrentlySelectedCase, preserve the loaded list when
        // refreshing the same case so a late findCase response does not wipe the UI.
        const existing = state.currentlySelected.case
        const sameCase = existing && String(existing.id) === String(currentCase.id)
        if (sameCase && existing.conversations) {
          currentCase.conversations = existing.conversations
        } else {
          currentCase.conversations = []
        }
      }
      if (!currentCase.conversationCount) {
        currentCase.conversationCount = 0
      }
      state.currentlySelected.case = currentCase
      Vue.set(state.currentlySelected, "viewedBy", [...currentlyViewing])
      if (!currentCase.notes) {
        currentCase.notes = []
      }
      for (const note of currentCase.notes) {
        note.editing = false
      }
    }
    state.currentlySelected.case = currentCase
  },
  setCurrentCaseConversations(state, { conversations, conversationCount, page, pageSize, hasMore }) {
    if (!state.currentlySelected.case) {
      return
    }
    Vue.set(state.currentlySelected.case, "conversations", conversations || [])
    if (typeof conversationCount === "number") {
      Vue.set(state.currentlySelected.case, "conversationCount", conversationCount)
    }
    Vue.set(state.currentlySelected, "conversationsPagination", {
      page: page || 1,
      pageSize: pageSize || 100,
      total: typeof conversationCount === "number" ? conversationCount : (state.currentlySelected.conversationsPagination.total || 0),
      hasMore: !!hasMore
    })
  },
  appendCurrentCaseConversations(state, { conversations, conversationCount, page, pageSize, hasMore }) {
    if (!state.currentlySelected.case) {
      return
    }
    const existing = state.currentlySelected.case.conversations || []
    const appended = (conversations || []).filter((conv) => !existing.find((e) => e.id === conv.id))
    Vue.set(state.currentlySelected.case, "conversations", existing.concat(appended))
    if (typeof conversationCount === "number") {
      Vue.set(state.currentlySelected.case, "conversationCount", conversationCount)
    }
    Vue.set(state.currentlySelected, "conversationsPagination", {
      page: page || state.currentlySelected.conversationsPagination.page || 1,
      pageSize: pageSize || state.currentlySelected.conversationsPagination.pageSize || 100,
      total: typeof conversationCount === "number" ? conversationCount : (state.currentlySelected.conversationsPagination.total || 0),
      hasMore: !!hasMore
    })
  },
  updateCaseProperties(state, props) {
    if (props) {
      for (const prop of Object.keys(props)) {
        Vue.set(state.currentlySelected.case.properties, prop, props[prop])
      }
      if (props.current_state) {
        Vue.set(state.currentlySelected.case, "currentState", props.current_state)
      }
    } else {
      console.warn("Called updateCaseProperties with no props.")
    }
  },
  setCurrentCaseViewers(state, viewers) {
    if (viewers) {
      Vue.set(state.currentlySelected, "viewers", [...viewers])
    } else {
      Vue.set(state.currentlySelected, "viewers", [])
    }
  },
  addCaseTasklist(state, data) {
    const current = state.currentlySelected.case
    current.tasklists.push(data.caseTasklist)
    Vue.set(state.currentlySelected.case, "tasklists", [...current.tasklists])
  },
  addCaseTask(state, data) {
    const tasklists = state.currentlySelected.case.tasklists.map((tasklist) => tasklist.id)
    const index = tasklists.indexOf(data.tasklistId)
    state.currentlySelected.case.tasklists[index].tasks.push(data.caseTask)
  },
  updateCaseTasklist(state, tasklist) {
    const tasklists = state.currentlySelected.case.tasklists.map((tasklist) => tasklist.id)
    const index = tasklists.indexOf(tasklist.id)
    state.currentlySelected.case.tasklist[index] = tasklist
  },
  updateCaseTask(state, data) {
    const { tasklistIndex, taskIndex } = indices(data.caseTask.id, state)
    state.currentlySelected.case.tasklists[tasklistIndex].tasks[taskIndex] = data.caseTask
  },
  updateCaseTaskStatus(state, data) {
    const { tasklistIndex, taskIndex } = indices(data.taskId, state)
    state.currentlySelected.case.tasklists[tasklistIndex].tasks[taskIndex].currentStatus = data.currentStatus
    Vue.set(state.currentlySelected, "case", state.currentlySelected.case)
  },
  updateCaseTaskId(state, data) {
    const { tasklistIndex, taskIndex } = indices(data.oldId, state)
    const newTask = state.currentlySelected.case.tasklists[tasklistIndex].tasks[taskIndex]
    newTask.id = data.newId
    state.currentlySelected.case.tasklists[tasklistIndex].tasks[taskIndex] = newTask
  },
  removeCaseTasklist(state, id) {
    const tasklists = state.currentlySelected.case.tasklists.map((tasklist) => tasklist.id)
    const index = tasklists.indexOf(id)
    if (index >= 0) state.currentlySelected.case.tasklists.splice(index, 1)
  },
  removeCaseTask(state, task) {
    const idParts = task.split("-")
    const tasklistId = idParts[0] + "-" + idParts[1] + "-" + idParts[2]
    const tasklists = state.currentlySelected.case.tasklists.map((tasklist) => tasklist.id)
    const tasklistIndex = tasklists.indexOf(tasklistId)
    const tasks = state.currentlySelected.case.tasklists[tasklistIndex].tasks.map((task) => task.id)
    const taskIndex = tasks.indexOf(task)
    if (taskIndex >= 0) state.currentlySelected.case.tasklists[tasklistIndex].tasks.splice(taskIndex, 1)
  },
  forceCurrentCase(state, currentCase) {
    state.currentlySelected.case = currentCase
  },
  setCurrentCaseAttachments(state, { attachments }) {
    Vue.set(state.currentlySelected.case, "fileAttachments", [...attachments])
  },
  caseViewersUpdate(state, { currentViewers, channel }) {
    const caseId = extractCaseIdFromChannelURL(channel)
    if (caseId === state.currentlySelected.case.id) {
      const newViewers = currentViewers || []
      Vue.set(state.currentlySelected, "viewedBy", [...newViewers])
    } else {
      console.error(`Ignoring viewer update for ${caseId}`, state.currentlySelected.case.id)
    }
  },
  setCaseBookmarked(state, isBookmarked) {
    state.currentlySelected.bookmarked = isBookmarked
  },
  addCaseNote(state, author) {
    const now = new Date()
    state.currentlySelected.case.notes.push({ subject: "", body: "", author, createdAt: now, updatedAt: now, editing: true })
    Vue.set(state.currentlySelected.case, "notes", [...state.currentlySelected.case.notes])
  },
  editCaseNote(state, { noteIndex, editing }) {
    const currentNotes = state.currentlySelected.case.notes
    const currentNote = state.currentlySelected.case.notes[noteIndex]
    if (!editing && !currentNote.id) {
      currentNotes.splice(noteIndex, 1)
    } else {
      currentNotes[noteIndex].editing = editing
      Vue.set(state.currentlySelected.case, "notes", [...currentNotes])
    }
  },
  deleteCaseNote(state, { noteIndex }) {
    const currentNotes = state.currentlySelected.case.notes
    currentNotes.splice(noteIndex, 1)
    Vue.set(state.currentlySelected.case, "notes", [...currentNotes])
  },
  updateCaseNote(state, { updatedNote, noteIndex }) {
    updatedNote.editing = false
    state.currentlySelected.case.notes[noteIndex] = updatedNote
    Vue.set(state.currentlySelected.case, "notes", [...state.currentlySelected.case.notes])
  },
  replyCaseConversation(state, { conversationId, conversationMessage }) {
    const kase = state.currentlySelected.case
    const convId = parseInt(conversationId, 10)
    const conversationIndex = kase.conversations.findIndex((conversation) => parseInt(conversation.id) === convId)
    if (conversationIndex >= 0) {
      const messages = state.currentlySelected.case.conversations[conversationIndex].messages
      messages.push(conversationMessage)
      Vue.set(state.currentlySelected.case.conversations[conversationIndex], "messages", [...messages])
    }
  },
  prepareNewCaseRelations(state, parentCase) {
    state.currentlySelected.parentCase = parentCase
  },
  clearNewCaseRelations(state) {
    state.currentlySelected.parentCase = null
  },
  markConversationAsRead(state, { conversationId, markRead }) {
    const kase = state.currentlySelected.case
    const conversationIndex = kase.conversations.findIndex((conversation) => parseInt(conversation.id) === conversationId)
    if (conversationIndex >= 0) {
      Vue.set(state.currentlySelected.case.conversations[conversationIndex], "unread", !markRead)
    }
  },
  setCaseTaskRunHistory(state, history) {
    Vue.set(state.currentlySelected, "taskHistory", history)
  },
  resolveTaskFailures(state, caseTask) {
    const kase = state.currentlySelected.case
    for (const tlIndex in kase.tasklists) {
      const tl = kase.tasklists[tlIndex]
      for (const tIndex in tl.tasks) {
        const t = tl.tasks[tIndex]
        if (t.id === caseTask.id) {
          Vue.set(state.currentlySelected.case.tasklists[tlIndex].tasks[tIndex], "unresolvedFailures", 0)
          return
        }
      }
    }
  },
  updateCaseFileAttachment(state, data) {
    const kase = state.currentlySelected.case
    const updatedFileAttachments = Object.assign([], kase.fileAttachments)
    const attachmentToUpdateIndex = updatedFileAttachments.findIndex((a) => a.id === data.id)

    if (attachmentToUpdateIndex !== -1) {
      updatedFileAttachments[attachmentToUpdateIndex] = data
      Vue.set(kase, "fileAttachments", updatedFileAttachments)
    } else {
      kase.fileAttachments.push(data)
    }
  },
  deleteCaseFileAttachment(state, fileAttachementId) {
    const kase = state.currentlySelected.case
    const attachmentIndex = kase.fileAttachments.findIndex((a) => a.id === fileAttachementId)
    if (attachmentIndex !== -1) {
      kase.fileAttachments.splice(attachmentIndex, 1)
    }
  },
  markCaseFileAttachmentHidden(state, fileAttachementId) {
    const kase = state.currentlySelected.case
    console.log("markCaseFileAttachmentHidden", fileAttachementId)
    const updatedFileAttachments = Object.assign([], kase.fileAttachments)
    const attachmentToUpdateIndex = updatedFileAttachments.findIndex((a) => a.id === fileAttachementId)
    console.log("found:", attachmentToUpdateIndex)
    if (attachmentToUpdateIndex !== -1) {
      updatedFileAttachments[attachmentToUpdateIndex].hidden = true
      console.log("updatedFileAttachments", updatedFileAttachments)
      Vue.set(kase, "fileAttachments", updatedFileAttachments)
    }
  },
  markCaseFileAttachmentVisible(state, fileAttachementId) {
    const kase = state.currentlySelected.case
    const updatedFileAttachments = Object.assign([], kase.fileAttachments)
    const attachmentToUpdateIndex = updatedFileAttachments.findIndex((a) => a.id === fileAttachementId)
    if (attachmentToUpdateIndex !== -1) {
      updatedFileAttachments[attachmentToUpdateIndex].hidden = false
      Vue.set(kase, "fileAttachments", updatedFileAttachments)
    }
  },
  setCaseFolders(state, { caseId, caseFolders }) {
    if (caseId && caseFolders) {
      const currentCase = state.currentlySelected
      if (currentCase) {
        Vue.set(currentCase, "caseFolders", caseFolders)
      }
    }
  },
  showInvalidFieldsDialog(state, { invalidFields, pendingCaseData }) {
    state.invalidFieldsDialog.show = true
    state.invalidFieldsDialog.invalidFields = invalidFields || []
    state.invalidFieldsDialog.pendingCaseData = pendingCaseData
  },
  hideInvalidFieldsDialog(state) {
    state.invalidFieldsDialog.show = false
    state.invalidFieldsDialog.invalidFields = []
    state.invalidFieldsDialog.pendingCaseData = null
  },
}
